import { useStore } from '@nanostores/react'
import clsx from 'clsx'
import React, { useState } from 'react'

import Dialog from '@/components/Dialog'
import Icon from '@/components/Icon/Icon'
import { pushAddToWishlistEvent } from '@/scripts/tracking'
import { closeDialog } from '@/stores/dialog/actions'
import { $openedDialog } from '@/stores/dialog/computed'
import type {
  CMSProductCardItem,
  CMSProductCardVariant,
  CMSProductPage,
  CMSProductVariant,
} from '@/types/sanity'
import { captureException } from '@/utilities/logger-fe'

import './WaitlistDialog.scss'

type Props = {
  title: string
  product: CMSProductPage | CMSProductCardItem
  variant: CMSProductVariant | CMSProductCardVariant
}

const WaitlistDialog: React.FC<Props> = ({ title, product, variant }) => {
  const [email, setEmail] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState('')
  const [hasSuccess, setHasSuccess] = useState(false)

  const openedDialog = useStore($openedDialog)

  const isOpen =
    openedDialog?.id === 'waitlist' &&
    openedDialog?.payload?.variantId === variant.id

  const validateEmail = () => /@(.*)\./.test(email)

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault()
    setError('')
    setIsLoading(true)
    if (!validateEmail()) {
      setError('Please use a valid email address.')
      setIsLoading(false)
      return
    }

    const body = JSON.stringify({
      data: {
        type: 'back-in-stock-subscription',
        attributes: {
          channels: ['EMAIL'],
          profile: {
            data: {
              type: 'profile',
              attributes: {
                email,
              },
            },
          },
        },
        relationships: {
          variant: {
            data: {
              type: 'catalog-variant',
              id: `$shopify:::$default:::${variant.id}`,
            },
          },
        },
      },
    })

    let response

    try {
      response = await fetch(
        `https://a.klaviyo.com/client/back-in-stock-subscriptions/?company_id=${
          import.meta.env.PUBLIC_KLAVIYO_API_KEY
        }`,
        {
          method: 'POST',
          body,
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
            Revision: '2025-01-15',
          },
        },
      )
    } catch (error) {
      captureException(error)
    }

    if (!response?.ok) {
      setError('Something went wrong.')
      setIsLoading(false)
      return
    }

    setHasSuccess(true)

    pushAddToWishlistEvent(variant.price.toString(), {
      id: product.id.toString(),
      title: 'details' in product ? product.details.title : product.title,
      vendor: 'details' in product ? product.details.vendor : product.vendor,
      sku: variant.sku,
      variant_id: variant.id.toString(),
      price: variant.price.toString(),
      quantity: 1,
    })
  }

  const classes = [
    'buying-panel-waitlist-dialog',
    {
      'buying-panel-waitlist-dialog--error': error,
      'buying-panel-waitlist-dialog--loading': isLoading,
    },
  ]

  return (
    openedDialog && (
      <Dialog isOpen={isOpen} onClose={closeDialog}>
        <div className={clsx(classes)}>
          <h2 className="buying-panel-waitlist-dialog__title type-h3">
            {title}
          </h2>

          <p className="buying-panel-waitlist-dialog__text">
            Be the first to know when {title} is back in stock.
          </p>

          <form
            className="buying-panel-waitlist-dialog__form"
            onSubmit={handleSubmit}
          >
            <input
              className="buying-panel-waitlist-dialog__input"
              type="email"
              placeholder="Email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
            />

            <span
              aria-hidden={error ? 'false' : 'true'}
              className="buying-panel-waitlist-dialog__error type-label"
            >
              <Icon name="attention" />
              <span className="buying-panel-waitlist-dialog__message">
                {error}
              </span>
            </span>

            <div className="buying-panel-waitlist-dialog__footer">
              {!hasSuccess && (
                <button
                  className="buying-panel-waitlist-dialog__submit button button-primary"
                  type="submit"
                  disabled={isLoading}
                >
                  Add to Waitlist
                </button>
              )}

              {hasSuccess && (
                <p className="buying-panel-waitlist-dialog__success type-label">
                  You're in! We'll let you know when it's back.
                </p>
              )}
            </div>
          </form>
        </div>
      </Dialog>
    )
  )
}

export default WaitlistDialog
